import { Component, ElementRef, HostListener, Input, AfterViewInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Posting } from 'app/library/posting/models/posting.model';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { GeneralAnalyticsService } from 'app/services/general-analytics.service';
import { SwiperOptions } from 'swiper/types/swiper-options';
import { PostingsCarouselDialogComponent } from '../dialogs/postings-carousel-dialog/postings-carousel-dialog.component';
import { ExperienceWidgetContent } from '../models/experience-widget-content.model';

@Component({
    selector: 'app-postings-swipper',
    templateUrl: './postings-swipper.component.html',
    styleUrls: ['./postings-swipper.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PostingsSwipperComponent extends UnsubscribeOnDestroy implements AfterViewInit {
    @ViewChild('nextBtn', { static: false }) nextBtn!: ElementRef;
    @ViewChild('prevBtn', { static: false }) prevBtn!: ElementRef;
    @ViewChildren('postingsRef') postingsRef!: QueryList<ElementRef>;
    @ViewChild('swiperEl', { static: false }) swiperEl!: ElementRef;
    isPostingVisible: boolean = false;

    public swiperConfig: SwiperOptions = {
        a11y: { enabled: true },
        direction: 'horizontal',
        slidesPerView: 'auto',
        spaceBetween: 8,
        keyboard: false,
        mousewheel: false,
        scrollbar: false,
        pagination: false,
        allowTouchMove: false,
        slideVisibleClass: 'swiper-slide-visible',
        slideActiveClass: 'swiper-slide-active',
        loop: false,
        watchSlidesProgress: true,
        // Responsive breakpoints
        breakpoints: {
            // when window width is >= 1280px
            1280: {
                slidesPerView: 4,
                slidesPerGroup: 4,
            },
            1680: {
                slidesPerView: 5,
                slidesPerGroup: 5,
            },
        },
        navigation: {
            prevEl: '.postings-swiper-button-prev',
            nextEl: '.postings-swiper-button-next',
        },
    };

    isMobileView: boolean;
    currentSlideWrapper: HTMLElement;

    @HostListener('window:resize')
    getScreenSize(): void {
        // Align with Fuse gt-md / desktop layout (1280px): hover on laptops (incl. 1366), click on tablets
        const scrWidth = window.innerWidth;
        this.isMobileView = scrWidth < 1280;
    }

    onMouseEnter(posting: Posting, event: MouseEvent) {
        // Show slide hover active data only for desktop view
        if (!this.isMobileView) {
            const slideWrapper = event.currentTarget as HTMLElement;
            setTimeout(() => {
                if (this.currentSlideWrapper === slideWrapper) {
                    slideWrapper.classList.add('slide-hover-active');
                    this.sendPostingViewEvent('posting_clicked', posting);
                }
            }, 500);
        }
    }

    onMouseLeave(event: MouseEvent) {
        const slideWrapper = event.currentTarget as HTMLElement;
        slideWrapper.classList.remove('slide-hover-active');
        this.currentSlideWrapper = null;
    }

    onMouseMove(event: MouseEvent) {
        const slideWrapper = event.currentTarget as HTMLElement;
        this.currentSlideWrapper = slideWrapper;
    }

    @Input()
    featuredWidget: ExperienceWidgetContent;
    arrayOfViewedElements: string[] = [];

    constructor(private _router: Router, private _matDialog: MatDialog, private _locationBasedAnalyticsService: GeneralAnalyticsService) {
        super();
    }

    ngAfterViewInit(): void {
        this.swiperConfig.navigation = {
            prevEl: this.prevBtn.nativeElement,
            nextEl: this.nextBtn.nativeElement,
        };

        Object.assign(this.swiperEl.nativeElement, this.swiperConfig);
        this.swiperEl.nativeElement.initialize();

        const swiperContainer = this.swiperEl.nativeElement.shadowRoot.querySelector('.swiper') as HTMLElement;
        if (swiperContainer) {
            swiperContainer.style.overflow = 'visible';
        }

        window.addEventListener('scroll', this.isElementInViewPort, true);
        this.getScreenSize();
        this.isElementInViewPort(null);
    }

    openPostInDialog(index: number, isFirst: boolean, isLast: boolean) {
        // Open posting in dialog only for mobile view
        if (this.isMobileView) {
            const dialogRef = this._matDialog.open(PostingsCarouselDialogComponent, {
                width: '656px',
            });

            dialogRef.componentInstance.featuredWidget = this.featuredWidget;
            dialogRef.componentInstance.index = index;
            dialogRef.componentInstance.isFirst = isFirst;
            dialogRef.componentInstance.isLast = isLast;
        }
    }

    openPostingUrl(posting: Posting) {
        // Follow posting url on click only if it's not mobile view
        if (!this.isMobileView) {
            this.sendPostingViewEvent('posting_link_followed', posting);

            // If the url starts with the same domain as the custom domain (whole one, not just base domain) then open internally
            if (posting.url.includes(window.location.host)) {
                window.open(`${posting.url}`, '_self');
            } else {
                const currentPostUrl = posting.url;
                window.open(currentPostUrl, '_blank');
            }
        }
    }

    async sendPostingViewEvent(eventName: string, posting: Posting) {
        if (this._router.url === '/grow') {
            this._locationBasedAnalyticsService.createPostingEvent(
                eventName,
                posting.id,
                posting.postingType.id,
                posting.translation.title,
                null,
                this.featuredWidget.id,
                this.featuredWidget.name
            );
        }
    }

    isElementInViewPort = async (event: any): Promise<void> => {
        //checks if isScrolledIntoView
        if (this.postingsRef.length && this._router.url === '/grow') {
            for (let i = 0; i < this.postingsRef.toArray().length; i++) {
                const postingsRefArray = this.postingsRef.toArray();
                const postingElement = postingsRefArray[i];

                let currentPostId = postingElement.nativeElement.getAttribute('postId');
                let addedToArray = this.arrayOfViewedElements.some((postId) => postId === currentPostId);

                const rect = postingElement.nativeElement.getBoundingClientRect();
                const leftShown = rect.left >= 0;
                const rightShown = rect.right <= (window.innerWidth || document.documentElement.clientWidth);
                const topShown = rect.top >= 0;
                const bottomShown = rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
                this.isPostingVisible = topShown && bottomShown && leftShown && rightShown;

                if (!addedToArray && this.isPostingVisible) {
                    this.arrayOfViewedElements.push(currentPostId);

                    if (this.arrayOfViewedElements.includes(currentPostId)) {
                        const postingResource = this.featuredWidget.content.find((postings) => postings.posting.id === currentPostId);
                        this._locationBasedAnalyticsService.createPostingEvent(
                            'posting_viewed',
                            postingResource.posting.id,
                            postingResource.posting.postingType.id,
                            postingResource.posting.translation.title,
                            postingResource.audience,
                            this.featuredWidget.id,
                            this.featuredWidget.name
                        );
                    }
                }
            }
        }
    };

    onSlideChangeTransitionEnd() {
        this.isElementInViewPort(null);
    }

    ngOnDestroy() {
        window.removeEventListener('scroll', this.isElementInViewPort, true);
    }
}
